<script setup lang="ts">
import FormCode from '~/components/ui/FormCode.vue'
import { useAuthStore } from '~/store/auth'
import { trackAuthSuccessRedirect } from '~/tracking/scan/scanSection'

defineOptions({
	name: 'ModalVerifyEmail',
})

interface Props {
	modelValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
})

const { t: $t, locale } = useI18n()
const authStore = useAuthStore()
const route = useRoute()

const emit = defineEmits(['update:modelValue'])

const isCounting = ref<boolean>(false)
const countdown = ref(0)
const loadingCode = ref<boolean>(false)
const errorMessage = ref<string>('')
const oldCode = ref<string>('')
const disabledBtn = ref<boolean>(false)

const isOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
})

const emailVerify = computed(() => authStore.email_to_verify ?? '')

const openLogin = () => {
	authStore.SET_STATE_AUTH({
		type: 'showModalVerifyEmail',
		value: false,
	})
	authStore.SET_STATE_AUTH({
		type: 'showModalLogin',
		value: true,
	})
}

const resendEmail = async () => {
	if (isCounting.value) return
	isCounting.value = true
	countdown.value = 60

	const contentData = {
		email: emailVerify.value,
		lang: locale.value,
		redirect: route.fullPath,
	}

	const response = await getActionAuth(contentData)
	if (response && !response?.success) return

	await new Promise<void>((resolve) => {
		const interval = setInterval(() => {
			countdown.value--

			if (countdown.value <= 0) {
				clearInterval(interval)
				isCounting.value = false
				resolve()
			}
		}, 1000)
	})
}

const getActionAuth = async (contentData: any) => {
	if (authStore.type_modal_verify_email === 'login') return await authStore.login(contentData)

	return await authStore.resendActivation(contentData)
}

const authWithCode = async (contentData: any) => {
	if (authStore.type_modal_verify_email === 'login') return await authStore.loginWithCode(contentData)

	return await authStore.activationUserWithCode(contentData.code)
}

const setEventScan = (route: string) => {
	if (route.endsWith('/results/scan') === false) return
	trackAuthSuccessRedirect({
		source: 'scanner_section',
		redirect_destination: 'scan_ticket',
	})
}

const saveCode = async (code: any) => {
	loadingCode.value = true
	const response = await authWithCode({
		email: emailVerify.value,
		code: code?.code,
	})
	loadingCode.value = false

	if (response.success === false) {
		oldCode.value = code?.code
		disabledBtn.value = true
		errorMessage.value = response.error?.message || $t('Invalid code. Please check and try again')
		return
	}

	authStore.SET_STATE_AUTH({
		type: 'showModalVerifyEmail',
		value: false,
	})

	/* ✅ Redirección post-auth */
	const lastPath = getLastInternalPath()
	clearLastInternalPath()

	if (lastPath) {
		setEventScan(lastPath)

		await navigateTo(lastPath, { replace: true })
	}
}

const codeChange = (code: string) => {
	if (code === oldCode.value) {
		disabledBtn.value = true
		errorMessage.value = $t('Invalid code. Please check and try again')
		return
	} else {
		errorMessage.value = ''
		disabledBtn.value = false
	}
}
</script>

<template>
	<UModal
		v-model="isOpen"
		:transition="false"
		:ui="{
			overlay: {
				background: 'bg-[#040404D4] bg-opacity-80',
			},
			width: 'w-full sm:max-w-xl',
			container: 'items-center',
			padding: 'p-4 sm:p-4',
			background: 'bg-secondary-800 sm:bg-secondary-700',
			rounded: 'rounded-2xl',
		}"
	>
		<div class="rounded-2xl py-7 smm:py-12 w-full max-w-md sm:max-w-xl space-y-8 mx-auto">
			<!-- Title -->
			<div class="space-y-8 max-w-full smm:max-w-96 mx-auto px-4 sm:px-0">
				<div class="text-neutral-50 space-y-2.5 sm:space-y-6 text-center">
					<h1 class="font-semibold smm:font-bold text-xl sm:text-3xl">
						{{ $t('Verify your Email') }}
					</h1>
					<p
						v-if="isCounting"
						class="text-sm sm:text-base leading-6 sm:leading-normal max-w-96 smm:max-w-full mx-auto"
					>
						{{ $t('We have sent an email to') }} {{ emailVerify ? emailVerify : 'n***@e***.com' }}
						{{ $t('with next steps') }}.
					</p>
					<p
						v-else
						class="text-sm sm:text-base leading-6 sm:leading-normal max-w-96 smm:max-w-full mx-auto"
					>
						{{ $t("We've sent a 6-digit code and a magic link to your registered email address.") }}
					</p>
				</div>
				<div class="flex flex-col smm:flex-row justify-center items-center gap-5 pt-5">
					<div class="flex items-center gap-2.5">
						<UIcon
							name="i-logos:google-gmail"
							class="w-10 h-8"
						/>
						<ULink
							to="https://mail.google.com"
							rel="noopener noreferrer"
							target="_blank"
							class="underline text-white hover:text-opacity-80"
						>
							{{ $t('Open Gmail') }}
						</ULink>
					</div>
					<div class="flex items-center gap-2.5">
						<NuxtImg
							src="/cdn/outlook.webp"
							alt="Image empty"
							width="52"
							height="50"
							loading="eager"
							title="Outlook icon"
							class="w-auto"
						/>
						<ULink
							to="https://outlook.live.com"
							target="_blank"
							rel="noopener noreferrer"
							class="underline text-white hover:text-opacity-80"
						>
							{{ $t('Open Outlook') }}
						</ULink>
					</div>
				</div>

				<!-- Divider -->
				<div class="relative">
					<div class="absolute inset-0 flex items-center">
						<div class="w-full border-t border-white/[.08]" />
					</div>
					<div class="relative flex justify-center text-sm">
						<span class="lowercase sm:capitalize px-4 bg-secondary-800 sm:bg-secondary-700 text-neutral-100">{{
							$t('Or')
						}}</span>
					</div>
				</div>
			</div>
			<FormCode
				:countdown="countdown"
				:is-counting="isCounting"
				:email-verify="emailVerify"
				:error-message="errorMessage"
				:disabled-btn="disabledBtn"
				:loading="loadingCode"
				@open-login="openLogin"
				@resend-email="resendEmail"
				@submit="saveCode"
				@code-change="codeChange"
			/>
		</div>
	</UModal>
</template>
